import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ChatConversation,
  ChatId,
  ChatMessage,
  ChatStatus
} from "@redelair-sa/intiza-ui";
import AssistantService from "../services/AssistantService";
import CompanyService from "../services/CompanyService";
import { TranslationService } from "../services/TranslationService";
import { AssistantChatErrorKind } from "../entities/company/Assistant/AssistantChat";

const STORAGE_KEY_PREFIX = "intiza_assistant_conversation";

// Conversaciones por usuario + empresa (GDC-5449): la clave evita restaurar una ajena.
function getStorageKey() {
  return `${STORAGE_KEY_PREFIX}_${CompanyService.getCompanyId()}_${CompanyService.getUserid()}`;
}

function readStoredConversationId(): number | null {
  try {
    const value = Number(sessionStorage.getItem(getStorageKey()));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function storeConversationId(conversationId: number | null) {
  try {
    if (conversationId === null) {
      sessionStorage.removeItem(getStorageKey());
    } else {
      sessionStorage.setItem(getStorageKey(), String(conversationId));
    }
  } catch {
    // sessionStorage no disponible: la conversación solo vive en memoria.
  }
}

function getErrorMessage(kind: AssistantChatErrorKind, message?: string) {
  switch (kind) {
    case "insufficient_balance":
      return TranslationService.translate.AIInsufficientBalance;
    case "unauthorized":
      return TranslationService.translate.AIAssistantSessionExpired;
    default:
      return message || TranslationService.translate.AssistantServiceError;
  }
}

/**
 * Estado del Asistente IA (GDC-2950/2951/2952). Vive en el header, que no se desmonta al
 * navegar, así que la conversación se mantiene entre pantallas; el `conversationId` además
 * se guarda en sessionStorage para recuperarla al recargar.
 */
export function useAssistantChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [activityLabel, setActivityLabel] = useState<string>();
  const [error, setError] = useState<string>();
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  const conversationIdRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const loadRequestRef = useRef(0);
  const localIdRef = useRef(0);
  const conversationsLoadedRef = useRef(false);
  const restoredRef = useRef(false);

  const updateConversationId = useCallback((id: number | null) => {
    conversationIdRef.current = id;
    setConversationId(id);
    storeConversationId(id);
  }, []);

  const abortCurrent = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => abortCurrent, [abortCurrent]);

  const refreshConversations = useCallback(async () => {
    setConversationsLoading(true);
    const result = await AssistantService.getConversations();
    setConversationsLoading(false);
    if (result instanceof Error) {
      return;
    }
    conversationsLoadedRef.current = true;
    setConversations(
      result.map((conversation) => ({
        id: conversation.conversationId,
        title: conversation.title,
        updatedAt: conversation.updatedAt
      }))
    );
  }, []);

  const openConversation = useCallback(
    async (id: ChatId) => {
      const targetId = Number(id);
      const requestId = ++loadRequestRef.current;
      abortCurrent();
      setStatus("idle");
      setActivityLabel(undefined);
      setError(undefined);
      setMessages([]);
      updateConversationId(targetId);

      const result = await AssistantService.getConversationMessages(targetId);
      if (requestId !== loadRequestRef.current) {
        return;
      }
      if (result instanceof Error) {
        updateConversationId(null);
        setError(TranslationService.translate.AssistantServiceError);
        return;
      }
      setMessages(
        [...result]
          .sort((a, b) => a.seq - b.seq)
          .map((message) => ({
            id: `seq-${message.seq}`,
            role: message.role,
            content: message.content
          }))
      );
    },
    [abortCurrent, updateConversationId]
  );

  const startNewConversation = useCallback(() => {
    loadRequestRef.current++;
    abortCurrent();
    setMessages([]);
    setStatus("idle");
    setActivityLabel(undefined);
    setError(undefined);
    updateConversationId(null);
  }, [abortCurrent, updateConversationId]);

  /** Recupera la conversación guardada en sessionStorage (solo la primera vez que se abre). */
  const restoreConversation = useCallback(() => {
    if (restoredRef.current) {
      return;
    }
    restoredRef.current = true;
    const storedId = readStoredConversationId();
    if (storedId !== null && conversationIdRef.current === null) {
      openConversation(storedId);
    }
  }, [openConversation]);

  const send = useCallback(
    (text: string) => {
      loadRequestRef.current++;
      abortCurrent();
      const controller = new AbortController();
      abortRef.current = controller;
      const isCurrent = () => abortRef.current === controller;
      const finish = () => {
        abortRef.current = null;
        setStatus("idle");
        setActivityLabel(undefined);
      };

      const userMessageId = `local-${++localIdRef.current}`;
      const assistantMessageId = `local-${++localIdRef.current}`;
      let assistantStarted = false;

      setMessages((prev) => [
        ...prev,
        { id: userMessageId, role: "user", content: text }
      ]);
      setStatus("thinking");
      setActivityLabel(undefined);
      setError(undefined);

      AssistantService.sendChatStreaming(
        { message: text, conversationId: conversationIdRef.current },
        {
          onConversationId: (id) => {
            if (isCurrent()) {
              updateConversationId(id);
            }
          },
          onTool: (tool) => {
            if (isCurrent()) {
              setActivityLabel(
                TranslationService.translate.AIAssistantConsulting.replace(
                  "{tool}",
                  tool
                )
              );
            }
          },
          onTextDelta: (delta) => {
            if (!isCurrent()) {
              return;
            }
            setActivityLabel(undefined);
            if (!assistantStarted) {
              assistantStarted = true;
              setStatus("streaming");
              setMessages((prev) => [
                ...prev,
                { id: assistantMessageId, role: "assistant", content: delta }
              ]);
              return;
            }
            setMessages((prev) =>
              prev.map((message) =>
                message.id === assistantMessageId
                  ? { ...message, content: message.content + delta }
                  : message
              )
            );
          },
          onDone: () => {
            if (!isCurrent()) {
              return;
            }
            finish();
            // El título de una conversación nueva se autogenera al cerrar el turno.
            if (conversationsLoadedRef.current) {
              refreshConversations();
            }
          },
          onError: (kind, message) => {
            if (!isCurrent()) {
              return;
            }
            finish();
            setError(getErrorMessage(kind, message));
          }
        },
        controller.signal
      );
    },
    [abortCurrent, refreshConversations, updateConversationId]
  );

  return {
    messages,
    status,
    activityLabel,
    error,
    conversationId,
    conversations,
    conversationsLoading,
    send,
    openConversation,
    startNewConversation,
    refreshConversations,
    restoreConversation
  };
}
